// src/components/RequireAuth.js

import React, { useContext } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext'; // Import AuthContext
import LoadingSpinner from './common/LoadingSpinner'; // Import LoadingSpinner

const RequireAuth = ({ children, requiredPermission }) => {
  const { user, isAuthenticated, loadingAuth, hasPermission } = useContext(AuthContext);
  const location = useLocation();

  if (loadingAuth) {
    // Show a global loading spinner while authentication status is being checked
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <LoadingSpinner />
      </div>
    );
  }

  // If not authenticated, redirect to login page
  if (!isAuthenticated) {
    // Use Navigate component to declaratively redirect
    // Pass current location to login page so it can redirect back after successful login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for active subscription or valid trial (unless on settings page or profile)
  const isPlatformUser = user?.role === 'super_admin' || user?.role === 'business_admin' || user?.role === 'support';
  const canManageSubscription = user?.role === 'org_admin' || user?.role === 'owner';
  
  const hasActiveSubscription = user?.subscription?.status === 'active' || user?.subscription?.status === 'trialing';
  const hasValidTrial = user?.subscription?.status === 'trial' && 
                        user?.subscription?.isTrial && 
                        new Date(user?.subscription?.trialEndsAt) > new Date();

  if (!isPlatformUser && !hasActiveSubscription && !hasValidTrial && !['/settings', '/profile'].includes(location.pathname)) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 p-6 text-center">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl border border-slate-100">
          <div className="w-20 h-20 bg-svinno-100 text-svinno-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Subscription Required</h2>
          <p className="text-slate-600 mb-8">
            {canManageSubscription 
              ? (user?.subscription?.status === 'trial' 
                  ? "Your 30-day trial has expired. To continue using the dashboard, please set up a paid subscription."
                  : "Your account is currently inactive. To access the dashboard, please complete your subscription setup.")
              : "Access is restricted because the organization's subscription is not active. Please contact your administrator."
            }
          </p>
          {canManageSubscription ? (
            <Link 
              to="/settings?tab=subscription" 
              className="block w-full bg-svinno-600 text-white font-bold py-3 rounded-xl hover:bg-svinno-700 transition shadow-lg shadow-svinno-500/20"
            >
              Set Up Subscription
            </Link>
          ) : (
            <button 
              onClick={() => window.location.href = '/login'} 
              className="block w-full bg-slate-100 text-slate-600 font-bold py-3 rounded-xl hover:bg-slate-200 transition"
            >
              Return to Login
            </button>
          )}
        </div>
      </div>
    );
  }

  // If a specific permission is required and the user doesn't have it
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-red-100 text-red-700 text-lg p-4 rounded-lg shadow-md">
        <svg className="w-16 h-16 mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path></svg>
        <p className="font-bold mb-2">Access Denied</p>
        <p className="text-center">You do not have the necessary permission to view this page: "{requiredPermission}".</p>
        <Link to="/dashboard" className="mt-6 text-blue-600 hover:underline font-medium">Go to Dashboard</Link>
      </div>
    );
  }

  // If authenticated and has permission, render the children components
  return children;
};

export default RequireAuth;
